import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { ContextMenuDividerComponent } from '@frontendlib/components/features/popover/components/context-menu-divider/context-menu-divider.component';
import { ContextMenuItemComponent } from '@frontendlib/components/features/popover/components/context-menu-item/context-menu-item.component';
import { CLOSE_POPOVER } from '@frontendlib/components/features/popover/types/close-popover.injection-token';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { relativeDateToWorkDayStart } from '@modules/common/utils/date';
import { injectFollowUpWording } from '@modules/common/utils/follow-up-wording';
import { type Observable, of, switchMap, tap, throwError } from 'rxjs';
import { BoardActionsService } from '../../../services/board-actions.service';
import { BoardListStore } from '../../../services/board-list.store';
import { BoardsStore } from '../../../services/boards.store';
import { BoardsQuickFormComponent } from '../../creation/boards-quick-form/boards-quick-form.component';
import { PanelBoardCreateComponent } from '../../creation/panel-board-create/panel-board-create.component';
import { injectBoardOpener } from '../../shared/board-opener';

interface BulkAction {
  title: string;
  execute: () => void;
}

/** What a board or folder made from a row's menu starts with. */
interface RowState {
  flagged?: true;
  followed?: Date;
  snoozed?: Date;
}

/** Rows that offer "Add new board" / "Add new folder" (Notes' words: "Add new note" / "Add new notebook"), the rows where Sheets offers
 * "Add New Workbook" / "Add New Folder". */
const ADDS_BOARD = ['all', 'recent', 'flagged', 'followup', 'snoozed'];
const ADDS_FOLDER = ['flagged', 'followup', 'snoozed', 'folders'];

/** Right-click menu of a sidebar row, in Sheets' order (`sheets/components/sidebar-context-menu`) without its "Settings", as
 * Slides has none (Wook, 2026-10-30: "Actually follow slides without a Settings option"):
 * - "Add new board" on All boards, Recent, Flagged, Follow Up and Snoozed: the folder menu's new-board form;
 * - "Add new folder" on Flagged, Follow Up, Snoozed and Folders: the rail "+" form;
 * - the row's bulk action: Unflag all / Remove all follow-ups (Notes' words) / Unsnooze all / Delete all (archived → Trash) / Empty trash.
 * A board or folder made from Flagged, Follow Up or Snoozed takes that state, as Sheets' does (`getCommonParams`). Renders
 * nothing on a row without any (Templates, search results). */
@Component({
  selector: 'app-boards-sidebar-context-menu',
  templateUrl: './boards-sidebar-context-menu.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ContextMenuItemComponent, ContextMenuDividerComponent, PanelBoardCreateComponent, BoardsQuickFormComponent],
})
export class BoardsSidebarContextMenuComponent {
  /** A plain `string` (the sidebar's `SidebarItem.id`, or `folders` for the Folders row); a row without entries renders nothing. */
  public readonly view = input.required<string>();
  /** The folder made from "Add new folder", for the sidebar to open as it opens one made with its "+". */
  public readonly folderCreated = output<string>();

  private readonly store = inject(BoardsStore);
  private readonly actions = inject(BoardActionsService);
  private readonly listStore = inject(BoardListStore, { optional: true });
  private readonly closePopover = inject(CLOSE_POPOVER, { optional: true });
  private readonly displayTimeZone = inject(DisplayTimeZoneService);
  /** "Remove all follow-ups", or "Remove all my follow-ups" where others share the workspace, as Notes' Follow Up row says it. */
  private readonly followUpWording = injectFollowUpWording();
  private readonly openBoard = injectBoardOpener();

  protected readonly addsBoard = computed(() => ADDS_BOARD.includes(this.view()));
  protected readonly addsFolder = computed(() => ADDS_FOLDER.includes(this.view()));

  protected readonly action = computed<BulkAction | null>(() => {
    const view: string = this.view();
    switch (view) {
      case 'flagged':
        return { title: 'Unflag all', execute: () => this.store.unflagAll() };
      case 'followup':
        return { title: this.followUpWording.removeAllFollowUps(), execute: () => this.store.unfollowUpAll() };
      case 'snoozed':
        return { title: 'Unsnooze all', execute: () => this.store.unsnoozeAll() };
      case 'archived':
        return { title: 'Delete all', execute: () => this.actions.deleteAllArchived() };
      case 'deleted':
        return { title: 'Empty trash', execute: () => this.actions.emptyTrash() };
      default:
        return null;
    }
  });

  /** As a folder's new board (`BoardsFolderContextMenuComponent.openCreated`): shown in the list and opened in the editor. */
  protected openCreated(id: string): void {
    const state = this.rowState();
    const board = this.store.boards().find((item) => item.id === id);
    if (state.flagged && board && !board.flagged) this.store.toggleFlag(id);
    if (state.followed) this.store.setFollowUp(id, state.followed);
    if (state.snoozed) this.store.setSnooze(id, state.snoozed);
    this.listStore?.ensureVisibleAfterCreate(id);
    this.close();
    this.openBoard(id);
  }

  /** The quick form waits for this before it closes, so the menu is still there to report the folder. `createFolder`
   * answers a failure with `null`; it becomes an error here, so the form keeps the title, says so and lets Save retry
   * (R01 MD-01). A new folder is never flagged (`createFolder`), so the toggle flags it. */
  protected readonly fileFolder = (title: string): Observable<string> => {
    const state = this.rowState();
    return this.store.createFolder(title).pipe(
      switchMap((id) => (id ? of(id) : throwError(() => new Error('The folder was not created.')))),
      tap((id) => {
        if (state.flagged) this.store.toggleFolderFlag(id);
        if (state.followed) this.store.updateFolderState(id, { followed: state.followed });
        if (state.snoozed) this.store.updateFolderState(id, { snoozed: state.snoozed });
        this.folderCreated.emit(id);
      }),
    );
  };

  protected close(): void {
    this.closePopover?.next();
  }

  /** Sheets' `getCommonParams`, taken when the item is made: flagged; the account's tomorrow 9am; now plus 5 minutes. */
  private rowState(): RowState {
    switch (this.view()) {
      case 'flagged':
        return { flagged: true };
      case 'followup':
        return { followed: relativeDateToWorkDayStart('tomorrow', this.displayTimeZone.timeZone()) };
      case 'snoozed': {
        const until = new Date();
        until.setMinutes(until.getMinutes() + 5);
        return { snoozed: until };
      }
      default:
        return {};
    }
  }
}
